import {useEffect, useRef, type SetStateAction} from 'react';
import equal from 'fast-deep-equal';
import {atom, useSetAtom} from 'jotai';
import {executeOrReturnAtomState} from '@netx/core-next/utils/jotai';
import {userPrefs} from '@netx/core-user/collections/singletons/userPreferences';
import {setPreference} from '@netx/core-user-next/utils/setPreference';
import {
	DEFAULT_SEARCH_SORT_FIELD,
	DEFAULT_SEARCH_SORT_ORDER,
	DEFAULT_SEARCH_MATCH_CRITERIA,
} from '../constants';
import type {SearchCriteria, SearchSort} from '../types';
import {
	areRulesValid,
	areSearchRulesSortable,
	getUserSort,
	updateUrlSearchString,
} from '../utils';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';

type SearchRulesState = Omit<SearchCriteria, 'sortField' | 'sortOrder'> & {};

const initialState: SearchRulesState = {
	elementTypes: [],
	elementSubType1s: [],
	elementSubType2s: [],
	elementValue1s: [],
	elementValue2s: [],
	elementValue3s: [],
	matchCriteria: DEFAULT_SEARCH_MATCH_CRITERIA,
};

export type SearchRuleConditions = Partial<SearchCriteria>;

export type UseSearchRulesParams = {
	conditions?: SearchRuleConditions;
	sortFieldPrefKey?: string;
	sortOrderPrefKey?: string;
};

export const useSearchRules = ({
	conditions,
	sortFieldPrefKey,
	sortOrderPrefKey,
}: UseSearchRulesParams = {}) => {
	const searchRulesAtom = useRef(
		atom(initialState, (get, set, value: SetStateAction<SearchRulesState>) => {
			const currentConditions = get(searchRulesAtom.current);
			const conditions = executeOrReturnAtomState(value, currentConditions);
			if (equal(conditions, currentConditions)) return;
			// TODO: this should happen in the code that uses this atom
			updateUrlSearchString({...conditions, ...get(searchSortAtom.current)});
			set(searchRulesAtom.current, conditions);
		}),
	);

	const searchRulesValidAtom = useRef(
		atom((get) => areRulesValid(get(searchRulesAtom.current))),
	);

	const searchSortAtom = useRef(
		atom(
			getUserSort({
				sortFieldPrefKey,
				sortOrderPrefKey,
				// defaultSortField: DEFAULT_SEARCH_SORT_FIELD,
				// defaultSortOrder: DEFAULT_SEARCH_SORT_ORDER,
			}),
			(get, set, value: SetStateAction<SearchSort>) => {
				const currentSort = get(searchSortAtom.current);
				const sort = executeOrReturnAtomState(value, currentSort);
				if (equal(sort, currentSort)) return;

				const conditions = get(searchRulesAtom.current);
				const sortable = areSearchRulesSortable(conditions);
				if (sortable) {
					if (sortFieldPrefKey && currentSort.sortField !== sort.sortField) {
						setPreference(sortFieldPrefKey, sort.sortField);
					}
					if (sortOrderPrefKey && currentSort.sortOrder !== sort.sortOrder) {
						setPreference(sortOrderPrefKey, sort.sortOrder);
					}
				}
				// TODO: this should happen in the code that uses this atom
				updateUrlSearchString({...conditions, ...sort});
				set(searchSortAtom.current, sort);
			},
		),
	);

	const setSearchRules = useSetAtom(searchRulesAtom.current);
	const setSearchSort = useSetAtom(searchSortAtom.current);

	const updateSearchRulesForDashboard = useConstCallback(
		({sortField, sortOrder, ...rest}: SearchRuleConditions) => {
			setSearchRules((current) => ({...current, ...rest}));
			setSearchSort((current) => ({
				sortField: sortField ?? current.sortField,
				sortOrder: sortOrder ?? current.sortOrder,
			}));
		},
	);

	useEffect(() => {
		const {sortField, sortOrder, ...rest} = conditions || {};
		setSearchRules((current) => ({...current, ...rest}));
		setSearchSort((current) => ({
			sortField: sortField ?? current.sortField,
			sortOrder: sortOrder ?? current.sortOrder,
		}));
	}, [conditions]);

	useEffect(() => {
		if (!sortFieldPrefKey && !sortOrderPrefKey) return;
		const unsubs = [
			(() => {
				const listener = (pref: string) => {
					if (pref !== sortFieldPrefKey && pref !== sortOrderPrefKey) return;
					setSearchSort(
						getUserSort({
							sortFieldPrefKey,
							sortOrderPrefKey,
							defaultSortField: DEFAULT_SEARCH_SORT_FIELD,
							defaultSortOrder: DEFAULT_SEARCH_SORT_ORDER,
						}),
					);
				};
				userPrefs.on('change', listener);
				return () => userPrefs.off('change', listener);
			})(),
		];
		return () => {
			unsubs.forEach((unsub) => unsub());
		};
	}, [sortFieldPrefKey, sortOrderPrefKey]);

	return {
		searchRulesAtom: searchRulesAtom.current,
		searchRulesValidAtom: searchRulesValidAtom.current,
		searchSortAtom: searchSortAtom.current,
		setSearchRules,
		setSearchSort,
		updateSearchRulesForDashboard,
	};
};
